import {
    QueryBuilderColumn,
    QueryBuilderQueryConditionType,
    QueryBuilderQueryConditionTypeEnum,
    QueryBuilderReducerActionTypeEnum,
    QueryBuilderReducerDispatch,
    QueryBuilderStateQueryCondition,
    ThemeMixin
} from "../../../index";
import React, {FunctionComponent, useCallback} from "react";
import {Box, IconButton, Theme, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {AddOutlined, DeleteOutlined} from "@mui/icons-material";
import QueryBuilderRule from "../rule";

export interface QueryBuilderConditionProps {
    columns: QueryBuilderColumn[];
    condition: QueryBuilderStateQueryCondition;
    conditionIndex: number;
    dispatch: QueryBuilderReducerDispatch;
    nestedLevel?: number;
    disabled: boolean;
}

const QueryBuilderCondition: FunctionComponent<QueryBuilderConditionProps> = ({
                                                                                  columns,
                                                                                  condition,
                                                                                  conditionIndex,
                                                                                  dispatch,
                                                                                  disabled,
                                                                                  nestedLevel = 1,
                                                                              }) => {

    /**
     * Changes the condition's type in the reducer.
     */
    const onConditionConditionChanged = useCallback(async (value) => {
        if (!value)
            return;
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeCondition,
            payload: {
                conditionIndex: conditionIndex,
                condition: value as QueryBuilderQueryConditionType,
            },
        })
    }, [dispatch, conditionIndex])

    /**
     * Adds a new rule to the query.
     */
    const addRule = useCallback(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.AddRule,
            payload: {
                conditionIndex: conditionIndex,
            },
        })
    }, [dispatch, conditionIndex]);

    /**
     * Removes a condition from the query.
     */
    const deleteCondition = useCallback(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.DeleteCondition,
            payload: {
                conditionIndex: conditionIndex,
            },
        })
    }, [dispatch, conditionIndex]);

    /**
     * The sx style mixin for the condition's box.
     */
    const boxMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginInlineStart: nestedLevel * 4,
            position: 'relative',
            '&:before': {
                position: 'absolute',
                content: '""',
                top: -16,
                left: -20,
                width: 20,
                height: `calc(100% - 4px)`,
                borderLeft: `1px dashed ${theme.palette.secondary.main}`,
                borderBottom: `1px dashed ${theme.palette.secondary.main}`,
            },
            '&:after': {
                position: 'absolute',
                content: '""',
                top: `calc(50% + 1px)`,
                left: -20,
                height: `calc(50% - 3px)`,
                borderLeft: `1px dashed ${theme.palette.secondary.main}`,
            },
        };
    }, [nestedLevel])

    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return ({
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);

    /**
     * The sx style mixin for the condition's rules box.
     */
    const rulesBoxMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return ({
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            position: 'relative',
            marginInlineStart: nestedLevel * 4,
            '&:before': {
                position: 'absolute',
                content: '""',
                top: -16,
                left: -20,
                height: `calc(100% + 14px)`,
                borderLeft: `1px dashed ${theme.palette.secondary.main}`,
            },
        });
    }, [nestedLevel]);

    return (
        <>
            <Box sx={boxMixin}>
                <ToggleButtonGroup
                    orientation="horizontal"
                    disabled={disabled}
                    value={condition.condition}
                    exclusive
                    onChange={(e, value) => onConditionConditionChanged(value)}
                    sx={toggleButtonGroupMixin}
                >
                    <ToggleButton value={QueryBuilderQueryConditionTypeEnum.AND}>
                        <Tooltip
                            title={`Make the rules to have an AND in between them when performing the search, 
                            meaning that the results associated with this filters' rules must conform to all the rules of this condition together.`}
                            disableInteractive
                        >
                            <Typography variant={'overline'} fontWeight={600}>
                                AND
                            </Typography>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value={QueryBuilderQueryConditionTypeEnum.OR}>
                        <Tooltip
                            title={`Make the rules to have an OR in between them when performing the search, 
                            meaning that the results associated with this filters' rules must conform to any (1 or more) of the rules of this condition.`}
                            disableInteractive
                        >
                            <Typography variant={'overline'} fontWeight={600}>
                                OR
                            </Typography>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={'Add Rule'} disableInteractive>
                    <span>
                        <IconButton
                            color={'primary'}
                            onClick={addRule}
                            disabled={disabled}
                            sx={{backgroundColor: 'background.paper'}}
                        >
                        <AddOutlined/>
                    </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={'Remove Condition'} disableInteractive>
                    <span>
                        <IconButton
                            color={'error'}
                            onClick={deleteCondition}
                            disabled={disabled}
                            sx={{backgroundColor: 'background.paper'}}
                        >
                        <DeleteOutlined/>
                    </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Box sx={rulesBoxMixin}>
                {
                    condition.rules.map((rule, ruleIndex, array) => {
                        return (
                            <QueryBuilderRule
                                key={`condition-${conditionIndex}-rule-${ruleIndex}`}
                                columns={columns}
                                rule={rule}
                                disabled={disabled}
                                ruleIndex={ruleIndex}
                                conditionIndex={conditionIndex}
                                dispatch={dispatch}
                                nestedLevel={nestedLevel + 1}
                                last={ruleIndex === array.length - 1}
                            />
                        );
                    })
                }
            </Box>
        </>
    );
}


export default QueryBuilderCondition;
